/**
 * Legal copy for the public Privacy Policy and Terms of Use pages.
 *
 * Kept out of the i18n JSON on purpose: the prose is long and structured, so a
 * dedicated module is easier to maintain and review than escaped JSON strings.
 * Company-specific identity (name / address / email) is injected at render time
 * from the public /api/company endpoint. Fields that the app cannot know are
 * left as [bracketed placeholders] to be completed by the operator.
 *
 * NOTE: This is a good-faith template, not legal advice. Have it reviewed by a
 * qualified professional and complete the bracketed placeholders before relying
 * on it in production.
 */

export const LAST_UPDATED = {
  fr: '20 juillet 2026',
  en: 'July 20, 2026',
};

function entity(company, lang) {
  const missing = lang === 'en' ? '[company name]' : '[Nom de la société]';
  const missingEmail = lang === 'en' ? '[contact email]' : '[email de contact]';
  const missingAddr = lang === 'en' ? '[company address]' : '[adresse du siège]';
  const missingNumber = lang === 'en' ? '[company number]' : '[n° d’entreprise]';
  return {
    name: (company && company.name && company.name.trim()) || missing,
    email: (company && company.email && company.email.trim()) || missingEmail,
    address: (company && company.address && company.address.trim()) || missingAddr,
    website: (company && company.website && company.website.trim()) || '',
    number: (company && company.vat && company.vat.trim()) || missingNumber,
  };
}

/* ---------------------------------------------------------------- Privacy -- */

export function getPrivacyDoc(lang, company) {
  const c = entity(lang === 'en' ? company : company, lang);
  if (lang === 'en') {
    return {
      title: 'Privacy Policy',
      intro: [
        `This Privacy Policy explains how ${c.name} ("we", "us") collects, uses and protects personal data processed through the ExpertisR platform (the "Service"). We act as the data controller for this processing.`,
        'We are committed to processing personal data in accordance with the EU General Data Protection Regulation (GDPR) and applicable Belgian law.',
      ],
      sections: [
        {
          id: 'controller',
          h: 'Data controller',
          p: [
            `Data controller: ${c.name}, ${c.address}.`,
            `Contact for privacy matters: ${c.email}.`,
            `Company registration number (BCE/KBO): ${c.number}. Where a Data Protection Officer has been appointed, they can be reached at [DPO contact].`,
          ],
        },
        {
          id: 'data',
          h: 'What data we collect',
          p: ['Depending on how the Service is used, we may process the following categories of personal data:'],
          ul: [
            'Account data: name, email address and role, used to sign you in and identify you (authentication is handled by our self-hosted PocketBase component).',
            'Assignment and report data: information about expertise assignments, including the names, addresses and contact details of on-site clients and billing clients, report content, checklists and conclusions.',
            'Photos and annotations: images you upload to a report and any annotations added to them, which may incidentally contain personal data.',
            'Appointment data: dates, locations and notes of appointments; if you connect Google Calendar, related calendar events.',
            'Billing data: billing party details, amounts and VAT, processed to issue invoices via our InvoiceNinja component.',
            'Technical data: authentication token stored in your browser, and server logs (e.g. timestamps and error information) needed to operate and secure the Service.',
          ],
        },
        {
          id: 'purposes',
          h: 'Purposes and legal bases',
          p: ['We process personal data for the following purposes and on the following legal bases (Art. 6 GDPR):'],
          ul: [
            'Providing and operating the Service, including report creation, appointments and PDF export — performance of a contract.',
            'Issuing and managing invoices, and complying with accounting and tax obligations — legal obligation and performance of a contract.',
            'Securing the Service, preventing abuse and troubleshooting — legitimate interest.',
            'Optional AI assistance (see below) — legitimate interest in offering productivity features, subject to your control.',
          ],
        },
        {
          id: 'ai',
          h: 'AI assistance',
          p: [
            'The Service offers optional AI features that help draft or enrich report text and generate conclusions. These features are disabled by default and are enabled and configured by the administrator.',
            'When AI assistance is used, the relevant report text — and, at higher assistance levels, report images — is transmitted to the configured third-party AI provider (Google Gemini, OpenAI or Anthropic) solely to generate the requested output. Provider selection and API keys are controlled by the administrator.',
            'AI-generated content is provided as a suggestion and must be reviewed by the expert before use. Please refer to the respective provider’s privacy terms for how they handle data submitted through their API.',
          ],
        },
        {
          id: 'sharing',
          h: 'Sharing and processors',
          p: ['We do not sell personal data. We share data only with service providers acting as our processors, to the extent needed to operate the Service:'],
          ul: [
            'Hosting/infrastructure provider hosting the application and database.',
            'Google (Calendar) — only if you choose to connect calendar synchronisation.',
            'InvoiceNinja — for issuing and tracking invoices.',
            'AI providers (Google, OpenAI, Anthropic) — only when AI assistance is used, as described above.',
          ],
          after: [
            'Some providers may process data outside the European Economic Area. Where that is the case, appropriate safeguards (such as EU Standard Contractual Clauses) are relied upon.',
          ],
        },
        {
          id: 'retention',
          h: 'Data retention',
          p: [
            'We retain personal data for as long as necessary to provide the Service and to meet legal obligations. Reports and related assignment data are kept for the duration of the business relationship and archived thereafter as required. Invoicing data is retained for the statutory accounting period ([retention period, e.g. 7 or 10 years]). Server logs are kept for a limited period.',
          ],
        },
        {
          id: 'security',
          h: 'Security',
          p: [
            'We apply appropriate technical and organisational measures to protect personal data, including transport encryption (HTTPS), restricted and role-based access, and masking of sensitive credentials (such as API keys and tokens), which are never displayed in clear text or logged.',
          ],
        },
        {
          id: 'rights',
          h: 'Your rights',
          p: ['Subject to applicable law, you have the right to:'],
          ul: [
            'access your personal data and obtain a copy;',
            'request rectification of inaccurate data;',
            'request erasure of your data;',
            'request restriction of, or object to, certain processing;',
            'data portability where applicable;',
            'withdraw consent at any time, without affecting prior processing.',
          ],
          after: [
            `To exercise these rights, contact us at ${c.email}. You also have the right to lodge a complaint with the Belgian Data Protection Authority (Autorité de protection des données / Gegevensbeschermingsautoriteit, www.autoriteprotectiondonnees.be).`,
          ],
        },
        {
          id: 'cookies',
          h: 'Cookies and local storage',
          p: [
            'The Service uses strictly necessary browser storage to keep you signed in (an authentication token). It does not use advertising or third-party tracking cookies.',
          ],
        },
        {
          id: 'changes',
          h: 'Changes to this policy',
          p: [
            'We may update this Privacy Policy from time to time. The date below indicates the latest revision. Material changes will be communicated through the Service where appropriate.',
          ],
        },
        {
          id: 'contact',
          h: 'Contact',
          p: [`For any question regarding this policy or your personal data, contact us at ${c.email}.`],
        },
      ],
    };
  }

  // French (default)
  return {
    title: 'Politique de confidentialité',
    intro: [
      `La présente politique de confidentialité explique comment ${c.name} (« nous ») collecte, utilise et protège les données à caractère personnel traitées via la plateforme ExpertisR (le « Service »). Nous agissons en qualité de responsable du traitement.`,
      'Nous nous engageons à traiter les données personnelles conformément au Règlement général sur la protection des données (RGPD) et à la législation belge applicable.',
    ],
    sections: [
      {
        id: 'controller',
        h: 'Responsable du traitement',
        p: [
          `Responsable du traitement : ${c.name}, ${c.address}.`,
          `Contact pour les questions de confidentialité : ${c.email}.`,
          `Numéro d’entreprise (BCE) : ${c.number}. Lorsqu’un délégué à la protection des données a été désigné, il peut être contacté à l’adresse [contact DPO].`,
        ],
      },
      {
        id: 'data',
        h: 'Données que nous collectons',
        p: ['Selon l’utilisation du Service, nous pouvons traiter les catégories de données suivantes :'],
        ul: [
          'Données de compte : nom, adresse e-mail et rôle, utilisés pour vous authentifier et vous identifier (l’authentification est assurée par notre composant PocketBase auto-hébergé).',
          'Données de mission et de rapport : informations relatives aux missions d’expertise, notamment les nom, adresse et coordonnées des clients intervention et des clients facturation, le contenu des rapports, les points de contrôle et les conclusions.',
          'Photos et annotations : images que vous ajoutez à un rapport et annotations associées, susceptibles de contenir incidemment des données personnelles.',
          'Données de rendez-vous : dates, lieux et notes des rendez-vous ; si vous connectez Google Agenda, les événements de calendrier correspondants.',
          'Données de facturation : coordonnées du destinataire de la facture, montants et TVA, traités pour émettre les factures via notre composant InvoiceNinja.',
          'Données techniques : jeton d’authentification stocké dans votre navigateur et journaux serveur (horodatages, informations d’erreur) nécessaires au fonctionnement et à la sécurité du Service.',
        ],
      },
      {
        id: 'purposes',
        h: 'Finalités et bases légales',
        p: ['Nous traitons les données personnelles aux finalités et sur les bases légales suivantes (art. 6 RGPD) :'],
        ul: [
          'Fournir et exploiter le Service, y compris la rédaction des rapports, les rendez-vous et l’export PDF — exécution du contrat.',
          'Émettre et gérer les factures et respecter les obligations comptables et fiscales — obligation légale et exécution du contrat.',
          'Sécuriser le Service, prévenir les abus et diagnostiquer les incidents — intérêt légitime.',
          'Assistance IA optionnelle (voir ci-dessous) — intérêt légitime à proposer des fonctionnalités de productivité, sous votre contrôle.',
        ],
      },
      {
        id: 'ai',
        h: 'Assistance par intelligence artificielle',
        p: [
          'Le Service propose des fonctionnalités d’IA optionnelles qui aident à rédiger ou enrichir le texte des rapports et à générer des conclusions. Ces fonctionnalités sont désactivées par défaut ; elles sont activées et configurées par l’administrateur.',
          'Lorsque l’assistance IA est utilisée, le texte concerné du rapport — et, aux niveaux d’aide supérieurs, les images du rapport — est transmis au fournisseur d’IA tiers configuré (Google Gemini, OpenAI ou Anthropic) uniquement pour générer le résultat demandé. Le choix du fournisseur et les clés d’API sont contrôlés par l’administrateur.',
          'Le contenu généré par l’IA constitue une suggestion et doit être vérifié par l’expert avant utilisation. Nous vous invitons à consulter la politique de confidentialité du fournisseur concerné quant au traitement des données soumises via son API.',
        ],
      },
      {
        id: 'sharing',
        h: 'Partage et sous-traitants',
        p: ['Nous ne vendons pas de données personnelles. Nous ne les partageons qu’avec des prestataires agissant en qualité de sous-traitants, dans la mesure nécessaire au fonctionnement du Service :'],
        ul: [
          'Hébergeur / prestataire d’infrastructure hébergeant l’application et la base de données.',
          'Google (Agenda) — uniquement si vous choisissez de connecter la synchronisation de calendrier.',
          'InvoiceNinja — pour l’émission et le suivi des factures.',
          'Fournisseurs d’IA (Google, OpenAI, Anthropic) — uniquement lorsque l’assistance IA est utilisée, comme décrit ci-dessus.',
        ],
        after: [
          'Certains prestataires peuvent traiter des données en dehors de l’Espace économique européen. Le cas échéant, des garanties appropriées (telles que les clauses contractuelles types de l’UE) sont mises en œuvre.',
        ],
      },
      {
        id: 'retention',
        h: 'Durée de conservation',
        p: [
          'Nous conservons les données personnelles aussi longtemps que nécessaire pour fournir le Service et respecter nos obligations légales. Les rapports et données de mission associées sont conservés pendant la durée de la relation d’affaires puis archivés selon les besoins. Les données de facturation sont conservées pendant la durée légale de conservation comptable ([durée, p. ex. 7 ou 10 ans]). Les journaux serveur sont conservés pour une durée limitée.',
        ],
      },
      {
        id: 'security',
        h: 'Sécurité',
        p: [
          'Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger les données personnelles : chiffrement des échanges (HTTPS), accès restreint fondé sur les rôles, et masquage des secrets sensibles (tels que les clés d’API et jetons), qui ne sont jamais affichés en clair ni journalisés.',
        ],
      },
      {
        id: 'rights',
        h: 'Vos droits',
        p: ['Sous réserve du droit applicable, vous disposez des droits suivants :'],
        ul: [
          'accéder à vos données personnelles et en obtenir une copie ;',
          'demander la rectification des données inexactes ;',
          'demander l’effacement de vos données ;',
          'demander la limitation d’un traitement ou vous y opposer ;',
          'la portabilité des données, le cas échéant ;',
          'retirer votre consentement à tout moment, sans affecter les traitements antérieurs.',
        ],
        after: [
          `Pour exercer ces droits, contactez-nous à l’adresse ${c.email}. Vous avez également le droit d’introduire une réclamation auprès de l’Autorité de protection des données (APD) belge (www.autoriteprotectiondonnees.be).`,
        ],
      },
      {
        id: 'cookies',
        h: 'Cookies et stockage local',
        p: [
          'Le Service utilise un stockage navigateur strictement nécessaire pour vous maintenir connecté (un jeton d’authentification). Il n’utilise pas de cookies publicitaires ni de traceurs tiers.',
        ],
      },
      {
        id: 'changes',
        h: 'Modifications de la présente politique',
        p: [
          'Nous pouvons mettre à jour la présente politique de temps à autre. La date indiquée ci-dessous précise la dernière révision. Les modifications importantes seront communiquées via le Service lorsque cela est approprié.',
        ],
      },
      {
        id: 'contact',
        h: 'Contact',
        p: [`Pour toute question relative à la présente politique ou à vos données personnelles, contactez-nous à l’adresse ${c.email}.`],
      },
    ],
  };
}

/* ------------------------------------------------------------------ Terms -- */

export function getTermsDoc(lang, company) {
  const c = entity(company, lang);
  if (lang === 'en') {
    return {
      title: 'Terms of Use',
      intro: [
        `These Terms of Use ("Terms") govern access to and use of the ExpertisR platform (the "Service") provided by ${c.name} (the "Provider"). By accessing or using the Service, you agree to these Terms. If you do not agree, do not use the Service.`,
      ],
      sections: [
        {
          id: 'service',
          h: '1. The Service',
          p: [
            'ExpertisR is a platform for creating and managing expertise reports. It provides step-by-step report drafting, photo upload and annotation, PDF export, appointment and lead management, invoicing through an InvoiceNinja integration, and optional AI assistance.',
          ],
        },
        {
          id: 'accounts',
          h: '2. Accounts and access',
          p: [
            'Accounts are created and managed by the Provider or its administrator. You are responsible for keeping your credentials confidential and for all activity carried out under your account. Notify the administrator promptly of any unauthorised use.',
          ],
        },
        {
          id: 'acceptable',
          h: '3. Acceptable use',
          p: ['You agree not to:'],
          ul: [
            'use the Service in violation of any applicable law or third-party rights;',
            'upload unlawful content or content you are not entitled to process;',
            'attempt to gain unauthorised access to, disrupt, or compromise the security or integrity of the Service;',
            'reverse engineer, resell or misuse the Service outside its intended purpose.',
          ],
        },
        {
          id: 'content',
          h: '4. Your content',
          p: [
            'You retain ownership of the reports, images and other content you submit ("Content"). You grant the Provider a limited licence to host, process and display the Content solely to operate the Service on your behalf. You are responsible for the accuracy and lawfulness of the Content, including having the necessary rights and consents for any personal data it contains.',
          ],
        },
        {
          id: 'ai',
          h: '5. AI assistance',
          p: [
            'Where enabled, AI features generate suggestions to assist drafting. AI output is provided "as is", may be inaccurate or incomplete, and does not constitute professional advice. The expert remains solely responsible for reviewing, correcting and validating the final report. Use of AI features may transmit report data to third-party AI providers as described in the Privacy Policy.',
          ],
        },
        {
          id: 'thirdparty',
          h: '6. Third-party services',
          p: [
            'The Service integrates third-party services (including Google Calendar, InvoiceNinja and AI providers). Your use of those integrations may be subject to the respective third party’s terms and privacy policies. The Provider is not responsible for third-party services.',
          ],
        },
        {
          id: 'ip',
          h: '7. Intellectual property',
          p: [
            'The Service, its software and its design are and remain the property of the Provider and its licensors. These Terms do not grant you any right in the Service other than the limited right to use it in accordance with these Terms.',
          ],
        },
        {
          id: 'availability',
          h: '8. Availability',
          p: [
            'The Service is provided on an "as available" basis. The Provider does not warrant uninterrupted or error-free operation and may perform maintenance, updates or changes that temporarily affect availability.',
          ],
        },
        {
          id: 'liability',
          h: '9. Limitation of liability',
          p: [
            'To the extent permitted by law, the Provider shall not be liable for indirect or consequential damages, loss of data or loss of profits arising from the use of, or inability to use, the Service. Nothing in these Terms limits liability that cannot be excluded under applicable law.',
          ],
        },
        {
          id: 'privacy',
          h: '10. Personal data',
          p: [
            'Processing of personal data through the Service is described in our Privacy Policy, which forms an integral part of these Terms.',
          ],
        },
        {
          id: 'termination',
          h: '11. Suspension and termination',
          p: [
            'The Provider may suspend or terminate access in the event of a breach of these Terms or where required by law. Upon termination, provisions that by their nature should survive (including intellectual property, liability and governing law) will remain in effect.',
          ],
        },
        {
          id: 'law',
          h: '12. Governing law and jurisdiction',
          p: [
            'These Terms are governed by Belgian law. Any dispute relating to the Service shall be subject to the exclusive jurisdiction of the courts of [judicial district], without prejudice to any mandatory consumer protection rules.',
          ],
        },
        {
          id: 'changes',
          h: '13. Changes to these Terms',
          p: [
            'The Provider may update these Terms from time to time. The date below indicates the latest revision. Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.',
          ],
        },
        {
          id: 'contact',
          h: '14. Contact',
          p: [`For any question regarding these Terms, contact us at ${c.email}.`],
        },
      ],
    };
  }

  // French (default)
  return {
    title: 'Conditions d’utilisation',
    intro: [
      `Les présentes conditions d’utilisation (« Conditions ») régissent l’accès et l’utilisation de la plateforme ExpertisR (le « Service ») fournie par ${c.name} (le « Fournisseur »). En accédant au Service ou en l’utilisant, vous acceptez les présentes Conditions. Si vous ne les acceptez pas, n’utilisez pas le Service.`,
    ],
    sections: [
      {
        id: 'service',
        h: '1. Le Service',
        p: [
          'ExpertisR est une plateforme de création et de gestion de rapports d’expertise. Elle offre la rédaction guidée de rapports, l’ajout et l’annotation de photos, l’export PDF, la gestion des rendez-vous et des leads, la facturation via une intégration InvoiceNinja, ainsi qu’une assistance IA optionnelle.',
        ],
      },
      {
        id: 'accounts',
        h: '2. Comptes et accès',
        p: [
          'Les comptes sont créés et gérés par le Fournisseur ou son administrateur. Vous êtes responsable de la confidentialité de vos identifiants et de toute activité réalisée depuis votre compte. Signalez sans délai à l’administrateur toute utilisation non autorisée.',
        ],
      },
      {
        id: 'acceptable',
        h: '3. Utilisation acceptable',
        p: ['Vous vous engagez à ne pas :'],
        ul: [
          'utiliser le Service en violation d’une loi applicable ou des droits de tiers ;',
          'téléverser un contenu illicite ou que vous n’êtes pas autorisé à traiter ;',
          'tenter d’accéder sans autorisation au Service, de le perturber ou d’en compromettre la sécurité ou l’intégrité ;',
          'procéder à de l’ingénierie inverse, revendre ou détourner le Service de sa finalité.',
        ],
      },
      {
        id: 'content',
        h: '4. Vos contenus',
        p: [
          'Vous conservez la propriété des rapports, images et autres contenus que vous soumettez (les « Contenus »). Vous accordez au Fournisseur une licence limitée pour héberger, traiter et afficher les Contenus dans le seul but d’exploiter le Service pour votre compte. Vous êtes responsable de l’exactitude et de la licéité des Contenus, notamment de disposer des droits et consentements nécessaires pour toute donnée personnelle qu’ils contiennent.',
        ],
      },
      {
        id: 'ai',
        h: '5. Assistance par IA',
        p: [
          'Lorsqu’elles sont activées, les fonctionnalités d’IA génèrent des suggestions destinées à faciliter la rédaction. Les résultats de l’IA sont fournis « en l’état », peuvent être inexacts ou incomplets et ne constituent pas un avis professionnel. L’expert demeure seul responsable de la relecture, de la correction et de la validation du rapport final. L’utilisation des fonctionnalités d’IA peut transmettre des données du rapport à des fournisseurs d’IA tiers, comme décrit dans la politique de confidentialité.',
        ],
      },
      {
        id: 'thirdparty',
        h: '6. Services tiers',
        p: [
          'Le Service intègre des services tiers (notamment Google Agenda, InvoiceNinja et des fournisseurs d’IA). L’utilisation de ces intégrations peut être soumise aux conditions et politiques de confidentialité propres à ces tiers. Le Fournisseur n’est pas responsable des services tiers.',
        ],
      },
      {
        id: 'ip',
        h: '7. Propriété intellectuelle',
        p: [
          'Le Service, son logiciel et son design sont et demeurent la propriété du Fournisseur et de ses concédants. Les présentes Conditions ne vous confèrent aucun droit sur le Service autre que le droit limité de l’utiliser conformément à celles-ci.',
        ],
      },
      {
        id: 'availability',
        h: '8. Disponibilité',
        p: [
          'Le Service est fourni « tel que disponible ». Le Fournisseur ne garantit pas un fonctionnement ininterrompu ou exempt d’erreurs et peut réaliser des opérations de maintenance, des mises à jour ou des modifications affectant temporairement la disponibilité.',
        ],
      },
      {
        id: 'liability',
        h: '9. Limitation de responsabilité',
        p: [
          'Dans les limites permises par la loi, le Fournisseur ne saurait être tenu responsable des dommages indirects ou consécutifs, de la perte de données ou de la perte de bénéfices résultant de l’utilisation ou de l’impossibilité d’utiliser le Service. Aucune disposition des présentes Conditions ne limite une responsabilité qui ne peut être exclue en vertu du droit applicable.',
        ],
      },
      {
        id: 'privacy',
        h: '10. Données personnelles',
        p: [
          'Le traitement des données personnelles via le Service est décrit dans notre politique de confidentialité, qui fait partie intégrante des présentes Conditions.',
        ],
      },
      {
        id: 'termination',
        h: '11. Suspension et résiliation',
        p: [
          'Le Fournisseur peut suspendre ou résilier l’accès en cas de manquement aux présentes Conditions ou lorsque la loi l’exige. Après résiliation, les dispositions qui, par leur nature, doivent survivre (notamment la propriété intellectuelle, la responsabilité et le droit applicable) restent en vigueur.',
        ],
      },
      {
        id: 'law',
        h: '12. Droit applicable et juridiction',
        p: [
          'Les présentes Conditions sont régies par le droit belge. Tout litige relatif au Service relève de la compétence exclusive des tribunaux de [arrondissement judiciaire], sans préjudice des règles impératives de protection des consommateurs.',
        ],
      },
      {
        id: 'changes',
        h: '13. Modifications des présentes Conditions',
        p: [
          'Le Fournisseur peut mettre à jour les présentes Conditions de temps à autre. La date indiquée ci-dessous précise la dernière révision. La poursuite de l’utilisation du Service après l’entrée en vigueur des modifications vaut acceptation des Conditions mises à jour.',
        ],
      },
      {
        id: 'contact',
        h: '14. Contact',
        p: [`Pour toute question relative aux présentes Conditions, contactez-nous à l’adresse ${c.email}.`],
      },
    ],
  };
}
